/**
 * Calendar-icon button that pops a MiniMonthCalendar to jump to a date.
 * Shared by the Pro home My Calendar panel and the full calendar page so the
 * date-jump affordance looks and behaves the same everywhere.
 */
import React, { useEffect, useRef, useState } from 'react';
import { addMonths, format, parseISO, subMonths } from 'date-fns';
import { Calendar } from 'lucide-react';
import MiniMonthCalendar from './MiniMonthCalendar';

interface DatePickerPopoverProps {
  /** Currently selected date (yyyy-MM-dd) — highlighted in the month grid. */
  selectedDate: string;
  onSelectDate: (dateStr: string) => void;
  /** Whether a day should show an event dot. */
  hasEvent?: (dateStr: string) => boolean;
  /** Align the popover to the left (default) or right edge of the button. */
  align?: 'left' | 'right';
  className?: string;
}

const DatePickerPopover: React.FC<DatePickerPopoverProps> = ({
  selectedDate,
  onSelectDate,
  hasEvent,
  align = 'left',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(() => parseISO(selectedDate));
  const containerRef = useRef<HTMLSpanElement>(null);
  const todayKey = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const select = (dateStr: string) => {
    onSelectDate(dateStr);
    setOpen(false);
  };

  return (
    <span ref={containerRef} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={() => {
          setMonth(parseISO(selectedDate));
          setOpen((prev) => !prev);
        }}
        aria-label="Pick a date"
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex items-center justify-center rounded-md p-0.5 text-primary-400 hover:text-primary-600 hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary"
      >
        <Calendar className="w-4 h-4" aria-hidden="true" />
      </button>
      {open && (
        <div
          className={`absolute top-full mt-1.5 z-30 w-64 rounded-xl border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-secondary shadow-lg p-2.5 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <MiniMonthCalendar
            monthDate={month}
            selectedDate={selectedDate}
            onSelectDate={select}
            hasEvent={hasEvent ?? (() => false)}
            onPrevMonth={() => setMonth((m) => subMonths(m, 1))}
            onNextMonth={() => setMonth((m) => addMonths(m, 1))}
            size="sm"
          />
          {selectedDate !== todayKey && (
            <button
              type="button"
              onClick={() => select(todayKey)}
              className="mt-1.5 w-full text-center text-xs font-semibold text-primary-600 hover:text-primary-700"
            >
              Back to today
            </button>
          )}
        </div>
      )}
    </span>
  );
};

export default DatePickerPopover;
